import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { styles } from '../view/styles';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { isEmpty } from 'lodash';
import { formatLargeNumber } from 'apps/webcomponents/src/utils/formatter.utils';
import { WEB_COMPONENT_COMPLIANCE_OVERVIEW } from 'apps/webcomponents/src/webcomponents.constants';
import { LicenseMessageFn } from '../../common/constant';

@customElement(WEB_COMPONENT_COMPLIANCE_OVERVIEW)
export class ComplianceOverviewWebComponent extends LitElement {
  @property({ type: Object, attribute: true, reflect: true })
  payload: {
    overallCompliance: number;
    compliantCount: number;
    nonCompliantCount: number;
    ignoredCount: number;
    totalChecks: number;
  };

  @property({ type: Boolean })
  loading = true;

  @property({ type: Boolean, attribute: false, reflect: false })
  hasAccessToComplianceStandard = false;

  @property({ type: Boolean, attribute: false, reflect: false })
  savedSmartView = false;

  strings = {
    items: [
      {
        key: 'overallCompliance',
        name: translateFn('Overall compliance'),
      },
      {
        key: 'compliantCount',
        name: translateFn('Compliant checks'),
        icon: html`<gz-icon color="var(--green-layout-100-primary-green)" size="20" name="ds-icon-notification_success" class="check-result"></gz-icon>`
      },
      {
        key: 'nonCompliantCount',
        name: translateFn('Non-compliant checks'),
        icon: html`<gz-icon color="var(--red-layout-300-primary)" size="20" name="ds-icon-delete_large" class="check-result"></gz-icon>`
      },
      {
        key: 'ignoredCount',
        name: translateFn('Ignored checks'),
        icon: html`<gz-icon size="20" class="check-result">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            slot="custom-svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.005 15.8399C13.2276 15.8399 15.84 13.2275 15.84 10.0049C15.84 6.78234 13.2276 4.16992 10.005 4.16992C6.78246 4.16992 4.17004 6.78234 4.17004 10.0049C4.17004 13.2275 6.78246 15.8399 10.005 15.8399ZM10.005 18.3399C14.6083 18.3399 18.34 14.6082 18.34 10.0049C18.34 5.40163 14.6083 1.66992 10.005 1.66992C5.40175 1.66992 1.67004 5.40163 1.67004 10.0049C1.67004 14.6082 5.40175 18.3399 10.005 18.3399Z"
              fill="var(--chart-105-solid)"
            ></path>
          </svg>
        </gz-icon>`
      },
    ],
  };

  static get styles() {
    return styles;
  }

  private renderLoaderItem(item, index: number): TemplateResult {
    return html`
      <div class="loader-item">
        <span class="loader-name fs-16">${item.name}</span>
        <gz-loader></gz-loader>
      </div>
      ${this.shouldAddSeparator(index)}
    `;
  }

  protected renderPremiumComplianceItem(key: string, value: string) {
    return this.savedSmartView ? '- -' : key === 'overallCompliance' ? value : this.hasAccessToComplianceStandard ? value : '***'; 
  }

  protected renderItem(item, index: number): TemplateResult {
    const value = !isEmpty(this.payload) && this.payload[item.key] ? this.payload[item.key] : 0;
    const initialValue = `${formatLargeNumber(value)}${item.key === 'overallCompliance' ? '%' : ''}`;
    const displayValue = this.payload['totalChecks'] !== 0 ? this.renderPremiumComplianceItem(item.key, initialValue) : '- -';

    return html`<div class="overview-section">
        <div class="section-title fs-16">
          <div class="d-flex align-items-center">
            ${item.icon ? item.icon : ''}
            <span class="ml-1 section-title">${item.name}</span>
          </div>
        </div>
        <span class="section-value fs-32">${displayValue}</span>
      </div>
      ${this.shouldAddSeparator(index)}`;
  }

  private renderLicenseInfo() {
    return html
    `<div class="info-section">
        <div class="section-title fs-8">
          <span class="section-title">
            <gz-icon name="ds-icon-notification_info" size="24" color="primary"></gz-icon>
            ${LicenseMessageFn.LICENSE_RESTRICTED_TITLE()}
          </span>
        </div>
        <div class="section-value fs-8">
          <span class="section-value">
            ${LicenseMessageFn.LICENSE_RESTRICTED_MESSAGE()}
          </span>
        </div>
      </div>
      ${this.shouldAddSeparator(0)}`;
  }

  private renderErrorIcon() {
    const tooltipError = translateFn(
      'An internal error prevented the widget from displaying data. Please contact Bitdefender Enterprise Support for more information.'
    );
    return html`<gz-tooltip tooltipText="${tooltipError}" class="error-tooltip">
      <gz-icon name="ds-icon-notification_negative" size="24" color="var(--red-chart-600)"></gz-icon>
    </gz-tooltip>`;
  }

  private shouldAddSeparator(index: number) {
    const numKeys = this.strings ? this.strings.items.length : 0;
    const addSeparator = index !== undefined && index + 1 < numKeys;
    return addSeparator ? html`<div class="separator"></div>` : nothing;
  }

  render() {
    return html`
      <div id="complianceOverviewContainer">
        ${this.savedSmartView ? this.renderLicenseInfo() : ''}
        ${this.strings.items.map((item, index) =>
          this.loading ? this.renderLoaderItem(item, index) : this.renderItem(item, index)
        )}
      </div
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [WEB_COMPONENT_COMPLIANCE_OVERVIEW]: ComplianceOverviewWebComponent;
  }
}
